import { format, isToday, isTomorrow } from 'date-fns';
import { de } from 'date-fns/locale/de';

import { WeatherIcon } from '#types/enums';

import type { ExternalWeatherReport } from './external-api-types';
import type { ValueWithUnit, WeatherReport } from './internal-api-types';

export function normalizeWeatherReport(weatherReport: ExternalWeatherReport): WeatherReport {
	const sortedDays = Object.values(weatherReport.days).sort(({ date: first }, { date: second }) => first - second);

	const normalizedDays = sortedDays.map(
		(day) =>
			({
				maxTemp: {
					value: day.maxtemperature.value,
					unit: mapTemperatureUnit(day.maxtemperature.Unit),
				},
				minTemp: {
					value: day.mintemperature.value,
					unit: mapTemperatureUnit(day.mintemperature.Unit),
				},
				precipitation: {
					value: day.precipitation.precipitationValue,
					unit: day.precipitation.precipitationUnit,
				},
				precipitationProbability: {
					value: day.precipitation.precipitationProbability,
					unit: day.precipitation.precipitationProbabilityUnit,
				},
				wind: {
					...convertValueUnit(day.windspeed),
					direction: day.windspeed.directionshort,
				},

				icon: getWeatherIconFromStyle(day.weathersituation.style),

				date: {
					humanReadable: getFormattedDay(day.date),
					timestamp: getDayTimestamp(day.date),
				},

				sections: Object.values(day.sections)
					.sort(({ date: a }, { date: b }) => a - b)
					.map((section) => ({
						icon: getWeatherIconFromStyle(section.weathersituation.style),
						time: {
							humanReadable: section.formattedhour,
							timestamp: format(section.date, "yyyy-MM-dd'T'HH:mm"),
						},
						temp: {
							value: section.temperature.value,
							unit: mapTemperatureUnit(section.temperature.Unit),
						},
					})),
			}) satisfies WeatherReport['days'][number]
	);

	const currentDay = sortedDays[0];

	const normalizedWeatherReport = {
		locationName: weatherReport.locationname,
		locationFullName: weatherReport.expandedname,
		zipCode: weatherReport.zipcode,
		today: {
			humanReadable: format(new Date(currentDay.date), 'EEEE, dd.MM.yyyy', { locale: de }),
			timestamp: getDayTimestamp(currentDay.date),
		},
		days: normalizedDays,
	} satisfies WeatherReport;

	// TODO filter duplicates
	// return filterDuplicateTimeOfDays(normalizedWeatherReport);
	return normalizedWeatherReport;
}

const STYLE_TO_WEATHER_ICON_REGEX = /[^a-z0-9]/gi;
const ALL_WEATHER_ICONS = new Set(Object.values(WeatherIcon));

function getFormattedDay(timestamp: number): string {
	if (isToday(timestamp)) {
		return 'Heute';
	} else if (isTomorrow(timestamp)) {
		return 'Morgen';
	} else {
		return format(timestamp, 'EEEEEE dd.MM.', { locale: de });
	}
}

function getDayTimestamp(timestamp: number) {
	const date = new Date(timestamp);

	return format(date, 'yyyy-MM-dd');
}

function getWeatherIconFromStyle(style: string): WeatherIcon | null {
	const convertedIconStyle = style.replace(STYLE_TO_WEATHER_ICON_REGEX, '_');

	if (!ALL_WEATHER_ICONS.has(convertedIconStyle as WeatherIcon)) {
		return null;
	}

	// `convertedIconStyle` is a valid `WeatherIcon`.
	return convertedIconStyle as WeatherIcon;
}

function convertValueUnit({ value, Unit: unit }: { value: string; Unit: string }): ValueWithUnit {
	return { value, unit };
}

function mapTemperatureUnit(unit: string): string {
	switch (unit) {
		case '°C':
			return '°';
		default:
			// TODO log warning
			return unit;
	}
}
